/**
 * FikoYT CAM — Post-processor: toolpath → G-code (ISO) ve APT. CATIA NC Code çıktısı muadili.
 */
import { IPartOperasyon, ICamOperasyon, ICamToolpath, ITakim } from './fiko-cam.types';

/** CATIA delme operasyonu → ISO sabit çevrim (canned cycle) kodu. */
const DELME_CEVRIM: Record<string, string> = {
  spotDrill: 'G82', drilling: 'G81', deepHole: 'G83', breakChips: 'G73',
  tapping: 'G84', reverseTap: 'G74',
  boring: 'G85', boringChamfer: 'G85', boringStop: 'G86', reaming: 'G85',
  counterBore: 'G82', counterSink: 'G82', chamfer2: 'G82', backBore: 'G87',
};
// threadMilling / threadNoTap / tslotting: sabit çevrim DEĞİL — helisel freze (toolpath hareketleri G01 olarak yazılır)
/** Dipte bekleme (dwell) gerektiren çevrimler. */
const DWELL_CEVRIM = new Set(['G82', 'G86']);

type V3 = { x: number; y: number; z: number };
const vnorm = (v: V3): V3 => { const l = Math.hypot(v.x, v.y, v.z) || 1; return { x: v.x / l, y: v.y / l, z: v.z / l }; };
const vcross = (a: V3, b: V3): V3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
const vdot = (a: V3, b: V3): number => a.x * b.x + a.y * b.y + a.z * b.z;
const isZ = (v?: V3): boolean => !v || (Math.abs(v.x) < 1e-9 && Math.abs(v.y) < 1e-9 && v.z > 0.999);

export class FikoCamPost {
  /** Tüm part operasyonu → ISO G-code (Fanuc tarzı). Koordinatlar PART-OPERASYON eksen sistemine göre; 3+2 op'lar G68.2 tilted plane + IJK. */
  static gcode(po: IPartOperasyon, toolpaths: Map<string, ICamToolpath>, takimlar: ITakim[]): string {
    const L: string[] = [];
    const n = (() => { let i = 10; return () => 'N' + (i += 10); })();
    // CATIA Machining Axis System: world → part-eksen-local dönüşümü
    const eo = po.eksenOrigin || { x: 0, y: 0, z: 0 };
    const eX = vnorm(po.eksenX || { x: 1, y: 0, z: 0 }), eY = vnorm(po.eksenY || { x: 0, y: 1, z: 0 }), eZ = vnorm(po.eksenZ || { x: 0, y: 0, z: 1 });
    const toPart = (p: V3): V3 => { const d = { x: p.x - eo.x, y: p.y - eo.y, z: p.z - eo.z }; return { x: vdot(d, eX), y: vdot(d, eY), z: vdot(d, eZ) }; };
    const dirPart = (d: V3): V3 => ({ x: vdot(d, eX), y: vdot(d, eY), z: vdot(d, eZ) });   // yön (origin'siz)
    L.push('%'); L.push('O0001 (' + (po.ad || 'FIKOYT_NC') + ')');
    L.push(n() + ' G21 G90 G94 G17 (mm, absolute, feed/min, XY)');
    L.push(n() + ' ' + (po.workOffset || 'G54') + ' (work offset)');
    let aktifTakim = '';
    for (const op of po.operasyonlar) {
      if (op.bastirilmis) continue;
      const tp = toolpaths.get(op.id); if (!tp || !tp.hareketler.length) continue;
      const tk = takimlar.find(t => t.id === op.takimId);
      L.push('(--- ' + (op.ad || op.tip) + ' ---)');
      if (tk && tk.id !== aktifTakim) {
        const tn = tk.numara ?? 1;
        L.push(n() + ' T' + tn + ' M06 (' + tk.ad + ' D' + tk.cap + ')');
        L.push(n() + ' S' + Math.round(op.hizlar.devir || 3000) + ' M03 M08 (sogutma)');
        aktifTakim = tk.id;
      }
      // CATIA Tool Axis (3+2): tool axis ≠ Z ise tilted work plane (G68.2) + koordinatlar o düzlemde
      const ta = op.takimYonu; const tilted = !isZ(ta?.yon);
      let TX = toPart;
      if (tilted && ta?.yon) {
        const w = vnorm(ta.yon); let up: V3 = Math.abs(w.z) > 0.9 ? { x: 0, y: 1, z: 0 } : { x: 0, y: 0, z: 1 };
        const u = vnorm(vcross(up, w)), v = vcross(w, u);
        const o = ta.origin || eo;
        TX = (p: V3): V3 => { const d = { x: p.x - o.x, y: p.y - o.y, z: p.z - o.z }; return { x: vdot(d, u), y: vdot(d, v), z: vdot(d, w) }; };
        const oP = toPart(o), kP = dirPart(w);   // tilted plane origin + tool axis (part-eksen yönünde)
        L.push(n() + ' G68.2 X' + oP.x.toFixed(3) + ' Y' + oP.y.toFixed(3) + ' Z' + oP.z.toFixed(3) + ' I' + kP.x.toFixed(5) + ' J' + kP.y.toFixed(5) + ' K' + kP.z.toFixed(5) + ' (tilted work plane / tool axis)');
        L.push(n() + ' G53.1 (tool axis dik hizalama)');
      }
      const cevrim = DELME_CEVRIM[op.tip]; const g = op.geometri;
      if (cevrim && g?.noktalar?.length) {
        const ust = (g.ustZ ?? 0) - (g.ofsetUst || 0);
        const rPlane = ust + 2;
        const zDip = (g.altZ ?? (ust - (g.derinlik ?? 10))) + (g.ofsetTaban || 0);
        const tap = op.tip === 'tapping' || op.tip === 'reverseTap' || op.tip === 'threadNoTap';
        const f = tap ? Math.round((op.hizlar.devir || 500) * (tk?.hatve || 1.5)) : Math.round(op.hizlar.ilerleme || 120);
        let ek = '';
        if (op.tip === 'deepHole' || op.tip === 'breakChips') ek += ' Q' + Math.abs(op.strateji?.peckDerinlik || 3).toFixed(2);
        if (DWELL_CEVRIM.has(cevrim)) ek += ' P' + Math.max(0, Math.round((op.strateji?.dwellDeger || 0) * 1000));
        const p0 = TX({ x: g.noktalar[0].x, y: g.noktalar[0].y, z: ust }); const dip = TX({ x: g.noktalar[0].x, y: g.noktalar[0].y, z: zDip });
        const rP = p0.z + 2;
        L.push(n() + ' G00 Z' + rP.toFixed(3) + ' (R düzlemi)');
        if (op.tip === 'boringStop') L.push(n() + ' M19 (oriented spindle stop)');
        L.push(n() + ' G98 ' + cevrim + ' X' + p0.x.toFixed(3) + ' Y' + p0.y.toFixed(3) + ' Z' + dip.z.toFixed(3) + ' R' + rP.toFixed(3) + ek + ' F' + f);
        for (let i = 1; i < g.noktalar.length; i++) { const pi = TX({ x: g.noktalar[i].x, y: g.noktalar[i].y, z: ust }); L.push(n() + ' X' + pi.x.toFixed(3) + ' Y' + pi.y.toFixed(3)); }
        L.push(n() + ' G80 (cevrim iptal)');
      } else {
        const comp = !!op.strateji?.kompanzasyonAktif && (op.tip === 'contouring' || op.tip === 'curveFollow');
        const g4x = op.strateji?.yon === 'conventional' ? 'G42' : 'G41';
        const dno = op.strateji?.kompanzasyon ?? (tk?.numara ?? 1);
        let px: number | null = null, py: number | null = null, compAcik = false;
        for (const m of tp.hareketler) {
          const P = TX(m); const x = P.x.toFixed(3), y = P.y.toFixed(3), z = P.z.toFixed(3);
          if (m.tip === 'rapid' || m.tip === 'retract') {
            if (compAcik) { L.push(n() + ' G40 (comp iptal)'); compAcik = false; }
            L.push(n() + ' G00 X' + x + ' Y' + y + ' Z' + z);
          } else if (m.arc && px !== null && py !== null) {
            const C = TX({ x: m.arc.cx, y: m.arc.cy, z: m.z });
            const I = (C.x - px).toFixed(3), J = (C.y - py).toFixed(3);
            L.push(n() + ' ' + (m.arc.cw ? 'G02' : 'G03') + ' X' + x + ' Y' + y + ' Z' + z + ' I' + I + ' J' + J + ' F' + Math.round(m.f || op.hizlar.ilerleme || 200));
          } else {
            if (comp && !compAcik) { L.push(n() + ' ' + g4x + ' D' + dno + ' (cutter comp)'); compAcik = true; }
            L.push(n() + ' G01 X' + x + ' Y' + y + ' Z' + z + ' F' + Math.round(m.f || op.hizlar.ilerleme || 200));
          }
          px = P.x; py = P.y;
        }
        if (compAcik) L.push(n() + ' G40 (comp iptal)');
      }
      if (tilted) L.push(n() + ' G69 (tilted plane iptal)');
    }
    L.push(n() + ' M09 (sogutma kapat)'); L.push(n() + ' M05'); L.push(n() + ' G00 Z' + (po.guvenlikZ || 50).toFixed(1));
    L.push(n() + ' M30'); L.push('%');
    return L.join('\n');
  }

  /** CATIA delme operasyonu → APT sabit çevrim tipi (CYCLE/…). threadMilling/tslotting helisel freze → GOTO. */
  private static readonly APT_CYCLE: Record<string, string> = {
    spotDrill: 'DRILL', drilling: 'DRILL', deepHole: 'DEEP', breakChips: 'BRKCHP',
    tapping: 'TAP', reverseTap: 'TAP', boring: 'BORE', boringChamfer: 'BORE', boringStop: 'BORE', reaming: 'REAM',
    counterBore: 'DRILL', counterSink: 'DRILL', chamfer2: 'DRILL', backBore: 'BORE',
  };

  /** APT (Automatically Programmed Tool) — BİRİNCİL ÇIKTI (makineden bağımsız CATIA ara formatı).
   * Tam kapsam: CYCLE sabit çevrimler · FEDRAT (feed değişiminde) · SPINDL/COOLNT her op · LOADTL takım değişimi ·
   * CUTCOM (cutter comp) · MULTAX + GOTO/x,y,z,i,j,k (3+2 / 5-axis tool axis). G-code bu formattan türetilir (ikincil). */
  static apt(po: IPartOperasyon, toolpaths: Map<string, ICamToolpath>, takimlar: ITakim[]): string {
    const L: string[] = [];
    const eo = po.eksenOrigin || { x: 0, y: 0, z: 0 };
    const eX = vnorm(po.eksenX || { x: 1, y: 0, z: 0 }), eY = vnorm(po.eksenY || { x: 0, y: 1, z: 0 }), eZ = vnorm(po.eksenZ || { x: 0, y: 0, z: 1 });
    const toPart = (p: V3): V3 => { const d = { x: p.x - eo.x, y: p.y - eo.y, z: p.z - eo.z }; return { x: vdot(d, eX), y: vdot(d, eY), z: vdot(d, eZ) }; };
    const dirPart = (d: V3): V3 => ({ x: vdot(d, eX), y: vdot(d, eY), z: vdot(d, eZ) });
    const tiltedVar = po.operasyonlar.some(o => !o.bastirilmis && !isZ(o.takimYonu?.yon));
    const multax = po.makineTip === '5axis' || tiltedVar;   // takım ekseni vektörleri GOTO'lara eklenir
    L.push('PARTNO/' + (po.ad || 'FIKOYT'));
    L.push('MACHIN/MILL,' + (po.makineTip === '5axis' ? '5AXIS' : '3AXIS'));
    L.push('UNITS/MM');
    if (multax) L.push('MULTAX/ON');
    L.push('$$ FikoYT CAM — APT (birincil NC ara formatı)');
    let aktifTakim = '';
    for (const op of po.operasyonlar) {
      if (op.bastirilmis) continue;
      const tp = toolpaths.get(op.id); if (!tp || !tp.hareketler.length) continue;
      const tk = takimlar.find(t => t.id === op.takimId);
      L.push('$$ --- ' + (op.ad || op.tip) + ' ---');
      if (tk && tk.id !== aktifTakim) {   // takım değişimi (Tool Change)
        L.push('CUTTER/' + tk.cap.toFixed(3) + (tk.kose ? ',' + tk.kose.toFixed(3) : ''));
        L.push('LOADTL/' + (tk.numara ?? 1));
        aktifTakim = tk.id;
      }
      L.push('SPINDL/' + Math.round(op.hizlar.devir || 3000) + ',RPM,CLW');
      L.push('COOLNT/ON');
      const comp = !!op.strateji?.kompanzasyonAktif && (op.tip === 'contouring' || op.tip === 'curveFollow');
      if (comp) L.push('CUTCOM/' + (op.strateji?.yon === 'conventional' ? 'RIGHT' : 'LEFT'));   // climb=LEFT (G41), conventional=RIGHT (G42)
      const cyc = FikoCamPost.APT_CYCLE[op.tip]; const g = op.geometri;
      if (cyc && g?.noktalar?.length) {
        // Sabit çevrim: CYCLE/… + delik başına GOTO (R-düzlemi kotunda) + CYCLE/OFF
        const ust = (g.ustZ ?? 0) - (g.ofsetUst || 0);
        const zDip = (g.altZ ?? (ust - (g.derinlik ?? 10))) + (g.ofsetTaban || 0);
        const derin = (ust - zDip) + (op.strateji?.breakthrough || 0);
        const tap = op.tip === 'tapping' || op.tip === 'reverseTap';
        const f = tap ? Math.round((op.hizlar.devir || 500) * (tk?.hatve || 1.5)) : Math.round(op.hizlar.ilerleme || 120);
        let cy = 'CYCLE/' + cyc + ',DEPTH,' + derin.toFixed(3) + ',MMPM,' + f + ',CLEAR,2.0';
        if (op.tip === 'deepHole' || op.tip === 'breakChips') cy += ',STEP,' + Math.abs(op.strateji?.peckDerinlik || 3).toFixed(2);
        if (op.tip === 'spotDrill' || op.tip === 'counterBore' || op.tip === 'counterSink' || op.tip === 'chamfer2') cy += ',DWELL,' + Math.max(0, op.strateji?.dwellDeger || 0);
        if (op.tip === 'reverseTap') cy += ',REVERS';
        if (op.tip === 'boringStop') cy += ',ORIENT';
        L.push(cy);
        for (const p of g.noktalar.filter(x => x.aktif !== false)) { const P = toPart({ x: p.x, y: p.y, z: p.z ?? ust }); L.push('GOTO/' + P.x.toFixed(4) + ',' + P.y.toFixed(4) + ',' + P.z.toFixed(4)); }
        L.push('CYCLE/OFF');
      } else {
        let sonF = -1;
        for (const m of tp.hareketler) {
          const P = toPart(m);
          const rapid = m.tip === 'rapid' || m.tip === 'retract';
          if (!rapid) { const f = Math.round(m.f || op.hizlar.ilerleme || 200); if (f !== sonF) { L.push('FEDRAT/' + f + ',MMPM'); sonF = f; } }
          let ijk = '';
          if (multax && m.i != null && m.j != null && m.k != null) { const a = dirPart({ x: m.i, y: m.j, z: m.k }); ijk = ',' + a.x.toFixed(6) + ',' + a.y.toFixed(6) + ',' + a.z.toFixed(6); }
          L.push((rapid ? 'RAPID\nGOTO/' : 'GOTO/') + P.x.toFixed(4) + ',' + P.y.toFixed(4) + ',' + P.z.toFixed(4) + ijk);
        }
      }
      if (comp) L.push('CUTCOM/OFF');
    }
    L.push('COOLNT/OFF'); L.push('SPINDL/OFF'); L.push('END'); L.push('FINI');
    return L.join('\n');
  }

  /** NC dokümantasyon (operasyon özeti, süre, takım listesi). */
  static dokuman(po: IPartOperasyon, toolpaths: Map<string, ICamToolpath>, takimlar: ITakim[]): string {
    const L: string[] = []; let toplamSure = 0;
    L.push('═══ NC DOKÜMAN — ' + (po.ad || 'Part Operation') + ' ═══');
    L.push('Makine: ' + po.makineTip + '  ·  Güvenlik Z: ' + po.guvenlikZ + ' mm');
    if (po.stok) L.push('Stok: ' + po.stok.en + '×' + po.stok.boy + '×' + po.stok.yuk + ' mm');
    L.push('');
    op_loop: for (const op of po.operasyonlar) {
      const tp = toolpaths.get(op.id); const tk = takimlar.find(t => t.id === op.takimId);
      const sure = tp?.sure || 0; toplamSure += sure;
      L.push('• ' + (op.ad || op.tip) + (op.bastirilmis ? ' [bastırıldı]' : ''));
      L.push('   Takım: ' + (tk ? tk.ad + ' Ø' + tk.cap : '—') + '  ·  Devir: ' + (op.hizlar.devir || '—') + ' rpm  ·  İlerleme: ' + (op.hizlar.ilerleme || '—') + ' mm/dk');
      L.push('   Yol: ' + (tp?.uzunluk || 0).toFixed(0) + ' mm  ·  Süre: ' + sure.toFixed(1) + ' dk');
    }
    L.push(''); L.push('TOPLAM SÜRE: ' + toplamSure.toFixed(1) + ' dk');
    return L.join('\n');
  }
}
